import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { PencilRuler, Scissors, Layers, Factory, Tag, Package } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMGS } from "@/data";
import { useLang } from "@/i18n";

const stepImgs = [IMGS.rack, IMGS.pattern, IMGS.fabric, IMGS.sewing, IMGS.qc, IMGS.factory, IMGS.labels, IMGS.shipping];
const icons = [PencilRuler, Scissors, Layers, Factory, Tag, Package];

const Process = () => {
  const { t } = useLang();
  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: lineRef, offset: ["start 0.75", "end 0.55"] });

  return (
    <section id="process" data-testid="process-section" className="border-t border-ink/10 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.process.overline}</p>
            <h2 data-testid="process-heading" className="mt-6 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              {t.process.h2a} <em className="italic text-clay">{t.process.h2b}</em>
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-ink/60">{t.process.sub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden border border-ink/10 bg-ink/10 md:grid-cols-3 lg:grid-cols-6">
          {t.process.caps.map((c, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={c.t} delay={i * 0.05} className="bg-white">
                <div data-testid={`cap-card-${i + 1}`} className="group flex h-full flex-col p-6 transition-colors duration-500 hover:bg-sand">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-clay/40 text-clay transition-colors duration-500 group-hover:bg-clay group-hover:text-bone">
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-5 font-serif text-lg leading-snug tracking-tight text-ink">{c.t}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-ink/60">{c.b}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div ref={lineRef} className="relative mt-20">
          <div className="absolute bottom-0 start-5 top-0 w-px bg-ink/10 md:start-6" aria-hidden="true" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute bottom-0 start-5 top-0 w-px origin-top bg-clay md:start-6"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-14 md:gap-20">
            {t.process.steps.map((s, i) => (
              <Reveal key={s.t} delay={0.05}>
                <div data-testid={`process-step-${i + 1}`} className="relative grid grid-cols-1 items-center gap-6 ps-16 md:grid-cols-12 md:ps-24">
                  <span
                    className="absolute start-0 top-1 flex h-11 w-11 items-center justify-center rounded-full border border-clay/50 bg-white font-serif text-lg text-clay md:h-13 md:w-13 md:h-[52px] md:w-[52px]"
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                  <div className="md:col-span-7">
                    <span className="inline-block rounded-full border border-clay/40 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-clay">
                      {s.time}
                    </span>
                    <h3 className="mt-4 font-serif text-2xl tracking-tight text-ink md:text-3xl">{s.t}</h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink/60 md:text-base">{s.b}</p>
                  </div>
                  <div className="md:col-span-5">
                    <div className="group h-36 overflow-hidden bg-sand md:h-44">
                      <img
                        src={stepImgs[i]}
                        alt={s.t}
                        loading="lazy"
                        className={i === 7
                          ? "h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                          : "h-full w-full object-cover grayscale-[40%] transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"}
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <p data-testid="process-promise" className="mt-20 max-w-3xl border-t border-ink/10 pt-10 font-serif text-2xl italic leading-snug tracking-tight text-ink md:text-3xl">
            “{t.process.promise}”
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Process;
